import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import useBaskets from "../../data/useBaskets";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import useStockPermissions, { STOCK_PRICE_LOCKED_MESSAGE } from "../../data/useStockPermissions";
import useNotification from "../notifications/useNotifications";
import { IBasketItem } from "../../models/Basket";
import { FormatType } from "../../models/EbayOffer";
import PriceFormat from "../../utils/priceFormat";
import { currencySymbol } from "../../utils/currency";
import { MAX_PART_PRICE } from "../../utils/priceLimits";

export default function BasketPriceEditor({ basketItem }: { basketItem: IBasketItem }) {
  const { t } = useTranslation("accounts");
  const { updateItem } = useBaskets("BasketPriceEditor");
  const { saveHandlers } = useDefaultCRUDHandlers(t("Basket Item"));
  const { addNotification } = useNotification();
  const { restricted } = useStockPermissions();
  const [editPrice, setEditPrice] = useState<string>("");

  // Stock:AdminOnlyChanges - a line that already has a price sells at it unless an admin changes
  // it. basketPrice is what the API compares with; it's set when the part goes in the basket.
  const priceLocked = restricted && (basketItem.basketPrice ?? 0) > 0;

  const getDefaultPrice = () => {
    if (!basketItem.part.offers || basketItem.part.offers.length === 0) {
      return basketItem.part.enteredPrice || 0;
    }

    const offer = basketItem.part.offers[0];
    if (offer.format === FormatType.FixedPrice) {
      return offer.buyItNowPrice || 0;
    } else {
      return offer.auctionStartPrice || 0;
    }
  };

  const getCurrentPrice = () => {
    return basketItem.basketPrice ?? getDefaultPrice();
  };

  // Initialize editPrice with current price when component mounts or basketItem changes
  useEffect(() => {
    setEditPrice(getCurrentPrice().toString());
  }, [basketItem]);

  const handlePriceUpdate = () => {
    if (priceLocked) return;

    const newPrice = parseFloat(editPrice);
    
    if (isNaN(newPrice) || newPrice < 0) {
      addNotification({
        primaryText: t("Invalid price entered"),
        variant: "warn"
      });
      setEditPrice(getCurrentPrice().toString()); // Reset to current price
      return;
    }

    if (newPrice > MAX_PART_PRICE) {
      addNotification({
        primaryText: t("Price cannot exceed {{value}}", { value: PriceFormat(MAX_PART_PRICE) }),
        variant: "warn"
      });
      setEditPrice(getCurrentPrice().toString()); // Reset to current price
      return;
    }

    updateItem.mutate({
      ...basketItem,
      basketPrice: newPrice
    }, saveHandlers);
  };

  return (
    <div className="flex flex-col items-center gap-y-2" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center gap-x-2">
        <span>{currencySymbol()}</span>
        <input
          value={editPrice}
          onChange={(e) => setEditPrice(e.target.value)}
          onBlur={handlePriceUpdate}
          step={0.01}
          min={0}
          max={MAX_PART_PRICE}
          readOnly={priceLocked}
          title={priceLocked ? t(STOCK_PRICE_LOCKED_MESSAGE) : undefined}
          className={`h-8 w-20 border p-1 text-sm${priceLocked ? " bg-gray-100 text-gray-600 cursor-not-allowed" : ""}`}
          type="number"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handlePriceUpdate();
            } else if (e.key === "Escape") {
              setEditPrice(getCurrentPrice().toString()); // Reset to current price
            }
          }}
        />
      </div>
    </div>
  );
}
